import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MODES } from "@/lib/research";

const STORAGE_KEY = "cs-composer-mode";
const withRef = (e) => (e?.requestId ? `${e.message} (Ref ${e.requestId})` : e?.message || "Search couldn't start.");
const savedMode = () => {
  try {
    const m = localStorage.getItem(STORAGE_KEY);
    return MODES[m] ? m : "chat";
  } catch {
    return "chat";
  }
};

// Composer mode (Chat / Fast Search / Deep Search), remembered until the user changes it,
// plus start() which runs a web search inline in a chat.
export function useResearchMode() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mode, setModeState] = useState(savedMode);
  const [starting, setStarting] = useState(false);
  const depth = MODES[mode].depth || null;

  const setMode = (m) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // Private mode: the choice just isn't remembered across reloads.
    }
  };

  const start = async ({ question, conversationId = null, projectId = null }) => {
    setStarting(true);
    try {
      const body = { question, depth, ...(conversationId ? { conversation_id: conversationId } : { new_chat: true }), ...(projectId && !conversationId ? { project_id: projectId } : {}) };
      const res = await api.post("/research/sessions", body);
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      if (conversationId) await qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      else navigate(`/chat/${res.conversation_id}`);
      return res;
    } catch (e) {
      toast.error(withRef(e));
      return null;
    } finally {
      setStarting(false);
    }
  };

  return { mode, setMode, on: Boolean(depth), depth, chatMode: MODES[mode].chatMode || "chat", starting, start };
}
